import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Card from "@/components/ui/Card";
import EmptyState from "@/components/ui/EmptyState";
import { ButtonLink } from "@/components/ui/Button";
import EventStatusBadge from "@/components/events/EventStatusBadge";
import { Link } from "@inertiajs/react";
import type { SatelliteEvent } from "@/interfaces/satellite_event";

export default function OrganizeIndex({
  events,
  helping,
  applications_open,
}: {
  events: SatelliteEvent[];
  helping: SatelliteEvent[];
  applications_open: boolean;
}) {
  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Organize"
          subtitle="Run a Boba Bash in your city. Apply once, and when it's approved you'll get a page people can RSVP to."
          actions={
            <div className="flex flex-wrap gap-3">
              <ButtonLink href="/guides/organizer-handbook" variant="secondary">
                Read the handbook
              </ButtonLink>
              {applications_open && (
                <ButtonLink href="/organize/new">Apply to organize</ButtonLink>
              )}
            </div>
          }
        />

        {!applications_open && events.length === 0 && (
          <Panel title="applications-closed.txt" tone="alt">
            <p className="text-body text-ink-muted">
              Organizer applications are closed right now. Keep an eye on the
              announcements - we open them up between rounds.
            </p>
          </Panel>
        )}

        {events.length === 0 && helping.length === 0 ? (
          applications_open && (
            <EmptyState
              title="You haven't applied yet"
              description="Tell us what city you're in and roughly what you're planning. It takes about five minutes."
              action={
                <ButtonLink href="/organize/new">
                  Start an application
                </ButtonLink>
              }
            />
          )
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {events.map((event) => (
              <OrganizerEventCard key={event.id} event={event} />
            ))}
          </div>
        )}

        {/* Events someone invited you to help run, as opposed to your own. */}
        {helping.length > 0 && (
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-heading text-ink font-bold">
              You're helping with
            </h2>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {helping.map((event) => (
                <OrganizerEventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function OrganizerEventCard({ event }: { event: SatelliteEvent }) {
  return (
    <Link href={`/organize/${event.id}`}>
      <Card interactive>
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-heading text-ink leading-tight font-bold">
            {event.name}
          </h2>
          <EventStatusBadge state={event.aasm_state} />
        </div>

        <p className="text-body text-ink-muted mt-1">
          {event.location_label} · {event.starts_at_short_label ?? "Date TBD"}
        </p>

        {event.aasm_state === "approved" &&
          event.checklist_progress &&
          event.checklist_progress.total > 0 && (
            <p className="text-caption text-ink-muted mt-3">
              Checklist: {event.checklist_progress.done}/
              {event.checklist_progress.total}
              {event.checklist_progress.complete ? " - all set" : ""}
            </p>
          )}

        {event.aasm_state === "approved" && (
          <p className="text-body text-ink mt-4">
            <span className="font-display text-title text-carrot-orange font-bold">
              {event.rsvp_count ?? 0}
            </span>{" "}
            {event.rsvp_count === 1 ? "person" : "people"} going
            {event.capacity ? ` · ${event.spots_left} spots left` : ""}
          </p>
        )}

        {event.aasm_state === "draft" &&
          (event.missing_fields?.length ?? 0) > 0 && (
            <p className="text-caption text-ink-soft mt-4">
              Still needs: {event.missing_fields!.join(", ")}
            </p>
          )}

        {event.aasm_state === "rejected" && event.review_notes && (
          <p className="text-caption text-ink-muted mt-4">
            {event.review_notes}
          </p>
        )}
      </Card>
    </Link>
  );
}
